<template>
  <div class="top-container">
    <div class="toolBarTitle">
      <div class="toolbar-meta">
        <el-button size="mini" class="toolbar-back-btn" icon="el-icon-arrow-left" @click="backClick">{{ $tt("返回") }}</el-button>
        <el-form :inline="true" size="mini" class="prop-config-form">
          <el-form-item>
            <el-input
              v-model="showProjectName"
              :placeholder="$tt('请输入内容')"
              @change="changeProjectName"
              clearable
            ></el-input>
          </el-form-item>
          <el-form-item v-for="p in pageSizeConfig" :key="p.key" :label="$tt(p.label)">
            <el-input-number
              v-model.number="projectStyleData[p.key]"
              style="width:70px;"
              :min="0"
              :precision="0"
              :controls="false"
            ></el-input-number>
          </el-form-item>
        </el-form>
      </div>
      <div class="toolbar-tools-scroll">
        <div class="toolbar-tools">
          <el-button
            v-for="item in toolIconArr"
            :key="item.key"
            size="mini"
            :disabled="iconDisabled(item.key)"
            class="tool-label-btn"
            @click="toolIconClick(item.key)"
          >
            <i :class="item.cls"></i>
            {{ $tt(item.tip) }}
          </el-button>
          <el-popover
            v-model="alignMenuVisible"
            placement="bottom-start"
            trigger="click"
            popper-class="toolbar-align-popover"
          >
            <div class="align-menu-panel">
              <div class="align-menu-section">
                <div class="align-menu-title">{{ $tt("对齐") }}</div>
                <div class="align-menu-grid align-menu-grid--5">
                  <button
                    v-for="item in alignMenuAlignItems"
                    :key="item.key"
                    type="button"
                    class="align-menu-item"
                    :disabled="iconDisabled(item.key)"
                    @click="handleAlignMenuClick(item.key)"
                  >
                    <i :class="item.cls"></i>
                    <span>{{ $tt(item.tip) }}</span>
                  </button>
                </div>
              </div>
              <div class="align-menu-section">
                <div class="align-menu-title">{{ $tt("分布") }}</div>
                <div class="align-menu-grid align-menu-grid--2">
                  <button
                    v-for="item in alignMenuDistributeItems"
                    :key="item.key"
                    type="button"
                    class="align-menu-item"
                    :disabled="iconDisabled(item.key)"
                    @click="handleAlignMenuClick(item.key)"
                  >
                    <i :class="item.cls"></i>
                    <span>{{ $tt(item.tip) }}</span>
                  </button>
                </div>
              </div>
            </div>
            <el-button
              slot="reference"
              size="mini"
              class="tool-label-btn align-menu-trigger"
              :disabled="alignMenuAllDisabled"
            >
              <i class="iconfont icon-left-alignment"></i>
              {{ $tt("对齐") }}
              <i class="el-icon-arrow-down align-menu-arrow"></i>
            </el-button>
          </el-popover>
          <el-button
            size="mini"
            class="tool-label-btn"
            @click="shortcutDialogVisible = true"
          >
            <i class="el-icon-notebook-2"></i>
            {{ $tt("快捷键") }}
          </el-button>
        </div>
      </div>
      <div class="toolbar-actions-right">
        <el-tooltip class="item" effect="dark" placement="bottom-end">
          <div slot="content" class="jhb-tip-content">
            此功能拓展工程设备对外接收消息。<br />
            1.指令集设置：配置后可实现接收外部消息联动内部指令触发。<br />
            2.按钮状态配置：配置后可实现接收外部消息联动内部按钮状态变更。<br />
            <span style="color: red;margin-right:5px;">*</span>此功能需和工程一起保存！请配置后及时保存当前工程避免数据丢失！
          </div>
          <el-button icon="el-icon-warning" type="text" class="jhb-tip-btn"></el-button>
        </el-tooltip>
        <el-button
          v-for="item in rightIconArr"
          :key="item.key"
          size="mini"
          class="toolbar-right-btn"
          :icon="item.cls"
          @click="rightToolIconClick(item.key)"
        >{{ $tt(item.tip) }}</el-button>
      </div>
    </div>
    <!-- 预览 -->
    <Preview v-model="isShowPreview" @change="handlePreviewChange" />
    <!-- 预览 -->
    <BatchSetBtns ref="batchSetBtnsDialog" @confirm="batchSetBtnsConfirm" />
    <!-- 交互包 -->
    <InteractionPackage ref="interPackRef"></InteractionPackage>
    <CanvasShortcutDialog :visible.sync="shortcutDialogVisible" />
  </div>
</template>

<script>
import generateID from "@/utils/generateID";
import toast from "@/utils/toast";
import { mapState } from "vuex";
import Preview from "@/components/Editor/Preview";
import { commonStyle, commonAttr } from "@/custom-component/component-list";
import {
  pruneCanvasErrorMap,
  initEmptyErrorMapForPages,
  shouldValidateField,
  hasCanvasValidationErrors,
  getValidateFn,
  findFirstCanvasValidationError,
  describeCanvasValidationError,
  walkPageLabel,
} from "@/utils/canvasValidation";
import {
  collectProjectPageIntegrityIssues,
  summarizePageIntegrityIssues,
} from "@/utils/canvasPageIntegrity";
import { requestFlushCurrentPageBeforeSave, requestEnsureAllProjectPagesBeforeSave } from "@/utils/pageIntegritySaveBus";
import eventBus from "@/utils/eventBus";
import { deepCopy, isEmpty, getComponentsByType } from "@/utils/utils";
import { clearCanvasSessionStorage, leaveCanvasEditor, isPageSkeleton, parseCanvasAllData, stringifyCanvasAllData, collectPageOptionIds } from "@/utils/canvasPageLoader";
import { coalescedSetProjectCache } from "@/utils/canvasDraftWriter";
import {
  collectOutOfBoundsComponentIds,
  isMainCanvasPage,
} from "@/utils/clampComponentsToCanvas";
import { ECOLOGICAL_SIGN } from '@/constants/editor';
import BatchSetBtns from '@/components/BatchSetBtns';
import InteractionPackage from './InteractionPackage';
import CanvasShortcutDialog from '@/components/CanvasShortcutDialog.vue';

import _ from "lodash";

export default {
  components: { Preview, BatchSetBtns, InteractionPackage, CanvasShortcutDialog },
  data() {
    return {
      ECOLOGICAL_SIGN,
      isShowPreview: false,
      needToChange: [
        "top",
        "left",
        "width",
        "height",
        "fontSize",
        "borderWidth",
      ],
      scale: "100%",
      timer: null,
      pageSizeConfig: [
        {
          label: "宽",
          key: "width",
        },
        {
          label: "高",
          key: "height",
        },
      ],
      project_name: "",
      isSaving: false,
      dataId: '',
      shortcutDialogVisible: false,
      toolIconArr: [
        { cls: 'iconfont icon-revoke', tip: '撤销', key: 'revoke' },
        { cls: 'iconfont icon-empty', tip: '清空', key: 'empty' },
        { cls: 'iconfont icon-save', tip: '保存', key: 'save' },
        { cls: 'iconfont icon-setup-batch', tip: '批量设置', key: 'batchBtn' },
        { cls: 'iconfont icon-combination', tip: '组合', key: 'combination' },
        { cls: 'iconfont icon-split', tip: '拆分', key: 'split' },
        { cls: 'iconfont icon-lock', tip: '锁定', key: 'lock' },
        { cls: 'iconfont icon-unlock', tip: '解锁', key: 'unlock' },
      ],
      alignMenuVisible: false,
      alignMenuAlignItems: [
        { cls: 'iconfont icon-left-alignment', tip: '左对齐', key: 'toLeft' },
        { cls: 'iconfont icon-right-alignment', tip: '右对齐', key: 'toRight' },
        { cls: 'iconfont icon-top-alignment', tip: '上对齐', key: 'toTop' },
        { cls: 'iconfont icon-bottom-alignment', tip: '下对齐', key: 'toBottom' },
        { cls: 'iconfont icon-vertical', tip: '垂直居中', key: 'vertical' },
      ],
      alignMenuDistributeItems: [
        { cls: 'iconfont icon-horizontal-alignment', tip: '水平等距', key: 'toHEqual' },
        { cls: 'iconfont icon-vertical-alignment', tip: '垂直等距', key: 'toVEqual' },
      ],
      rightIconArr: [
        { cls: 'el-icon-folder-opened', tip: '交互包', key: 'interactionPackage' },
        { cls: 'el-icon-view', tip: '预览', key: 'preview' },
        { cls: 'el-icon-folder-checked', tip: '保存工程', key: 'save' }
      ],
      // 暂停自动保存
      pauseAutoSave: false,
      lastAutoSaveTime: null,
      /** 上次已写入 vuex / redis 的画布快照，用于脏检查跳过无变更写入 */
      lastAutoSavePayload: null,
      /** 上次成功写入 Redis 的快照 */
      lastRedisPayload: null,
      /** 打开工程 / 上次成功落库后的内容指纹；不相等即 projectDirty */
      persistBaseline: null,
      /** 打开工程后的初始化/切页系统写回完成前，不当成用户改过 */
      dirtyCheckReady: false,
      /** 本地合并间隔 ms；Redis 同步间隔见 REDIS_SYNC_SECONDS */
      autoSaveLocalMs: 1000,
      redisSyncSeconds: 60,
      lastProjectCanvasW: null,
      lastProjectCanvasH: null,
    };
  },
  props: {
    projectName: String,
    changeProjectName: Function,
    isInitSave: Boolean
  },
  watch: {
    isInitSave: {
      immediate: true,
      handler: function(val) {
        if (val) {
          this.dirtyCheckReady = false;
          this.initAutoSave();
        }
      }
    },
    projectStyleData: {
      handler: function(val) {
        if (!val) {
          return;
        }
        const w = Number(val.width);
        const h = Number(val.height);
        const prevW = this.lastProjectCanvasW;
        const prevH = this.lastProjectCanvasH;
        if (
          isFinite(w) &&
          isFinite(h) &&
          prevW != null &&
          prevH != null &&
          (w !== prevW || h !== prevH)
        ) {
          if (w < prevW || h < prevH) {
            this.warnOutOfBoundsAfterResize(w, h);
          }
          this.$nextTick(() => {
            eventBus.$emit("locateCanvas");
          });
        }
        if (isFinite(w)) {
          this.lastProjectCanvasW = w;
        }
        if (isFinite(h)) {
          this.lastProjectCanvasH = h;
        }
        const next = {
          ...(this.canvasStyleData || {}),
          width: val.width,
          height: val.height,
          scale:
            val.scale != null
              ? val.scale
              : ((this.canvasStyleData && this.canvasStyleData.scale) || 100),
        };
        if (
          !this.canvasStyleData ||
          Number(this.canvasStyleData.width) !== Number(next.width) ||
          Number(this.canvasStyleData.height) !== Number(next.height) ||
          Number(this.canvasStyleData.scale) !== Number(next.scale)
        ) {
          this.$store.commit("setCanvasStyle", next);
        }
        this.persistCanvasStyleSession(next);
      },
      deep: true
    }
  },
  computed: {
    ...mapState([
      "componentData",
      "canvasStyleData",
      "projectStyleData",
      "areaData",
      "curComponent",
      "selectedCurrentPage",
      "pageOptions",
      "projectValidateMap",
      "cacheCanvasDataKey",
      "canvasAllData",
      "canvasLazyLoad",
      "canvasLoadedPageIds",
      "canvasServerButtonCount",
      "projectFileResouces",
      "componentDataPageId",
      "canvasPageSwitching",
      "isChildPage",
    ]),
    showProjectName: {
      get() {
        return this.projectName;
      },
      set(v) {
        this.changeProjectName(v);
      },
    },
    iconDisabled() {
      return (key) => {
        switch(key) {
          // 组合
          case "combination":
            return !this.areaData.components.length;
            break;
          // 拆分
          case "split":
            return !this.curComponent ||
                this.curComponent.isLock ||
                this.curComponent.compType === ECOLOGICAL_SIGN ||
                this.curComponent.component != 'Group';
            break;
          // 锁定
          case "lock":
            return !this.curComponent || this.curComponent.isLock;
            break;
          // 解锁
          case "unlock":
            return !this.curComponent || !this.curComponent.isLock;
            break;
          // 垂直居中
          case 'vertical':
            return !this.curComponent || this.curComponent.id === 'Back';
            break;
          // 左对齐
          case 'toLeft':
          case 'toRight':
          case 'toTop':
          case 'toBottom':
          case 'toHEqual':
          case 'toVEqual':
            return !this.areaData.components.length;
            break;
        }
        return false;
      }
    },
    alignMenuAllDisabled() {
      const keys = [
        ...this.alignMenuAlignItems.map((i) => i.key),
        ...this.alignMenuDistributeItems.map((i) => i.key),
      ];
      return keys.every((key) => this.iconDisabled(key));
    },
    /** 相对上次打开/落库是否改过工程数据 */
    projectDirty() {
      return !this.isCanvasUnchanged();
    }
  },
  created() {
    eventBus.$on("preview", this.preview);
    eventBus.$on("save", this.save);
    eventBus.$on("clearCanvas", this.clearCanvas);
    this.scale = this.canvasStyleData.scale;
  },
  beforeDestroy() {
    eventBus.$off("preview", this.preview);
    eventBus.$off("save", this.save);
    eventBus.$off("clearCanvas", this.clearCanvas);
  },
  destroyed() {
    this.pauseAutoSave = true;
    clearInterval(this.autoSaveCycle);
    leaveCanvasEditor(this.$store);
  },
  methods: {
    backClick() {
      this.leaveEditor();
    },
    getCanvasFingerprint() {
      let remainComponentData = {};
      try {
        remainComponentData = this.canvasAllData
          ? JSON.parse(this.canvasAllData)
          : {};
      } catch (e) {
        remainComponentData = {};
      }
      const canMergeCurrent =
        this.componentDataPageId != null &&
        String(this.componentDataPageId) === String(this.selectedCurrentPage);
      const canvas = {
        ...remainComponentData,
        ...(canMergeCurrent
          ? { [String(this.selectedCurrentPage)]: this.componentData }
          : {}),
      };
      const orderedCanvas = {};
      Object.keys(canvas)
        .sort()
        .forEach((k) => {
          orderedCanvas[k] = canvas[k];
        });
      const pack = sessionStorage.getItem("interactionPackageData") || "";
      const pages = (this.pageOptions || []).map((p) => [
        p && String(p.value),
        p && p.label,
        (p && p.children || []).map((c) => [c && String(c.value), c && c.label]),
      ]);
      const style = this.projectStyleData || {};
      return JSON.stringify({
        canvas: orderedCanvas,
        name: this.projectName || "",
        w: Number(style.width) || 0,
        h: Number(style.height) || 0,
        pack,
        pages,
      });
    },
    capturePersistBaseline() {
      this.persistBaseline = this.getCanvasFingerprint();
      this.dirtyCheckReady = true;
    },
    warnOutOfBoundsAfterResize(canvasW, canvasH) {
      const flashIds = new Set();
      const collectFromList = (list) => {
        collectOutOfBoundsComponentIds(list, canvasW, canvasH).forEach((id) =>
          flashIds.add(id)
        );
      };
      if (!this.isChildPage && this.componentData && this.componentData.length) {
        collectFromList(this.componentData);
      }
      let all = {};
      try {
        all = parseCanvasAllData(this.canvasAllData);
      } catch (e) {
        all = {};
      }
      const curKey =
        this.selectedCurrentPage != null ? String(this.selectedCurrentPage) : "";
      Object.keys(all).forEach((pid) => {
        const arr = all[pid];
        if (!Array.isArray(arr) || isPageSkeleton(arr) || !isMainCanvasPage(arr)) {
          return;
        }
        if (String(pid) === curKey && !this.isChildPage) {
          collectFromList(this.componentData);
          return;
        }
        collectFromList(arr);
      });
      if (!flashIds.size) {
        return;
      }
      this.$message.warning(
        this.$tt(
          "画布分辨率已缩小，部分组件仍位于新画布区域外（与当前分辨率不兼容），请调整位置或恢复尺寸。"
        )
      );
      eventBus.$emit("flashOutOfBounds", { ids: Array.from(flashIds) });
    },
    syncRouteAfterTemplateSave(newId) {
      if (!newId || !this.$route.query.isCommonTemplate) {
        return;
      }
      const query = { ...this.$route.query, id: newId };
      delete query.isCommonTemplate;
      this.$router.replace({ path: this.$route.path, query }).catch(() => {});
    },
    persistCanvasStyleSession(style) {
      const st = style || this.canvasStyleData || {};
      let stored = {};
      try {
        stored = JSON.parse(sessionStorage.getItem("canvasStyle") || "{}") || {};
      } catch (e) {
        stored = {};
      }
      const pageKey = this.selectedCurrentPage;
      const pageEntry = {
        ...(stored[pageKey] && typeof stored[pageKey] === "object"
          ? stored[pageKey]
          : {}),
        width: st.width,
        height: st.height,
        scale: st.scale,
      };
      sessionStorage.setItem(
        "canvasStyle",
        JSON.stringify({
          ...stored,
          width: st.width,
          height: st.height,
          scale: st.scale,
          ...(pageKey != null ? { [pageKey]: pageEntry } : {}),
        })
      );
    },
    isCanvasUnchanged() {
      if (!this.dirtyCheckReady || !this.persistBaseline) {
        return true;
      }
      return this.getCanvasFingerprint() === this.persistBaseline;
    },
    finishLeaveEditor() {
      const cacheKey = this.cacheCanvasDataKey;
      if (cacheKey) {
        this.$request.removeProjectCache(cacheKey).catch(() => {});
      }
      leaveCanvasEditor(this.$store);
      this.$router.push("/projects");
    },
    async leaveEditor() {
      this.pauseAutoSave = true;
      if (!this.projectDirty) {
        this.finishLeaveEditor();
        return;
      }
      let shouldSave = false;
      try {
        await this.$confirm(this.$tt("是否保存当前画布？"), this.$tt("提示"), {
          confirmButtonText: this.$tt("是"),
          cancelButtonText: this.$tt("否"),
          type: "warning",
          distinguishCancelAndClose: true,
          closeOnClickModal: false,
        });
        shouldSave = true;
      } catch (action) {
        // 点关闭/遮罩：留在编辑器；点「否」：不保存直接返回
        if (action === "close") {
          this.pauseAutoSave = false;
          return;
        }
      }
      if (shouldSave) {
        const ret = await this.saveProject();
        if (!ret) {
          this.pauseAutoSave = false;
          return;
        }
      }
      this.finishLeaveEditor();
    },
    async removeCachFun() {
      const loading = this.$loading({
        lock: true,
        spinner: 'el-icon-loading'
      });
      try {
        let res = await this.$request.removeProjectCache(this.cacheCanvasDataKey)
        console.log('removeCachFun---', res);
        this.$store.commit('removeCache')
      } catch (error) {
        this.$store.commit('removeCache')
      }
      loading.close();
    },
    rightToolIconClick(key) {
      switch (key) {
        case 'interactionPackage':
          this.$refs.interPackRef.show();
          break;
        case 'preview':
          this.preview();
          break;
        case 'save': 
          this.saveProject();
          break;
        default:
          break;
      }
    },
    toolIconClick(key) {
      if (this.iconDisabled(key)) {
        return;
      }
      switch (key) {
        // 撤销
        case "revoke":
          this.undo();
          break;
        // 清空画布
        case "empty":
          this.clearCanvas();
          break;
        case 'batchBtn':
          this.batchSetBtn();
          break;
        case "save":
          this.save(false);
          break;
        // 组合
        case "combination":
          this.compose();
          break;
        // 拆分
        case "split":
          this.decompose();
          break;
        // 锁定
        case "lock":
          this.lock();
          break;
        // 解锁
        case "unlock":
          this.unlock();
          break;
        // 垂直居中
        case 'vertical':
          this.handleVertical();
          break;
        // 左对齐
        case 'toLeft':
          this.handleLeft();
          break;
        // 右对齐
        case 'toRight':
          this.handleRight();
          break;
        // 上对齐
        case 'toTop':
          this.handleTop();
          break;
        // 下对齐
        case 'toBottom':
          this.handleBootom();
          break;
        // 水平等间距
        case 'toHEqual':
          this.handleHEqual();
          break;
        // 垂直等间距
        case 'toVEqual':
          this.handleVEqual();
          break;
        default:
          break;
      }
    },
    handleAlignMenuClick(key) {
      this.toolIconClick(key);
      this.alignMenuVisible = false;
    },
    batchSetBtnsConfirm(datas) {
      let map = {};
      datas.map((item) => {
        map[item.id] = item;
      });
      this.$store.commit("batchSetComponentAttr", map);
    },
    pageOperClick(key) {
      switch (key) {
        case 'batchBtn':
          this.batchSetBtn();
          break;
        case "preview":
          this.preview();
          break;
        case "save":
          this.save(false);
          break;
      }
    },
    batchSetBtn() {
      this.$refs.batchSetBtnsDialog.show();
    },
    canvasOperClick(key) {
      switch (key) {
        // 撤销
        case "revoke":
          this.undo();
          break;
        // 清空画布
        case "empty":
          this.clearCanvas();
          break;
        // 组合
        case "combination":
          this.compose();
          break;
        // 拆分
        case "split":
          this.decompose();
          break;
        // 锁定
        case "lock":
          this.lock();
          break;
        // 解锁
        case "unlock":
          this.unlock();
          break;
        // 垂直居中
        case 'vertical':
          this.handleVertical();
          break;
        // 左对齐
        case 'toLeft':
          this.handleLeft();
          break;
        // 右对齐
        case 'toRight':
          this.handleRight();
          break;
        // 上对齐
        case 'toTop':
          this.handleTop();
          break;
        // 下对齐
        case 'toBottom':
          this.handleBootom();
          break;
        // 水平等间距
        case 'toHEqual':
          this.handleHEqual();
          break;
        // 垂直等间距
        case 'toVEqual':
          this.handleVEqual();
          break;
      }
    },
    handleLeft() {
      this.$store.commit("componentsAlign", 'left');
      this.$store.commit("recordSnapshot");
    },
    handleRight() {
      this.$store.commit("componentsAlign", 'right');
      this.$store.commit("recordSnapshot");
    },
    handleTop() {
      this.$store.commit("componentsAlign", 'top');
      this.$store.commit("recordSnapshot");
    },
    handleBootom() {
      this.$store.commit("componentsAlign", 'bootom');
      this.$store.commit("recordSnapshot");
    },
    handleHEqual() {
      this.$store.commit("horizontalEqual");
      this.$store.commit("recordSnapshot");
    },
    handleVEqual() {
      this.$store.commit("verticalEqual");
      this.$store.commit("recordSnapshot");
    },
    handleVertical() {
      let finalTop = this.canvasStyleData.height / 2 - this.curComponent.style.height / 2;
      this.$store.commit('setShapeSingleStyle', { key: 'top', value: parseInt(finalTop) });
    },
    initAutoSave() {
      if (this.autoSaveCycle) {
        clearInterval(this.autoSaveCycle);
      }
      this.autoSaveCycle = setInterval(() => {
        if (this.pauseAutoSave) {
          return;
        }
        if (!this.projectDirty) {
          return;
        }
        const autoSaveTime = Date.now();
        let syncRedis = false;
        const difTime = this.lastAutoSaveTime
          ? autoSaveTime - this.lastAutoSaveTime
          : 0;
        if (!this.lastAutoSaveTime) {
          // 首次只记时间，不打 Redis
          this.lastAutoSaveTime = autoSaveTime;
        } else if (difTime >= this.redisSyncSeconds * 1000) {
          syncRedis = true;
          this.lastAutoSaveTime = autoSaveTime;
        }
        this.save(true, !syncRedis);
      }, this.autoSaveLocalMs);
    },
    getAllPictures(data) {
      let parsed = {};
      try {
        parsed = typeof data === "string" ? JSON.parse(data) : data || {};
      } catch (e) {
        return [];
      }
      const acc = [];
      const seen = typeof WeakSet !== "undefined" ? new WeakSet() : null;
      const walk = (node) => {
        if (!node || typeof node !== "object") {
          return;
        }
        if (seen) {
          if (seen.has(node)) {
            return;
          }
          seen.add(node);
        }
        if (this.isFileResource(node)) {
          acc.push(node);
        }
        if (Array.isArray(node)) {
          node.forEach(walk);
          return;
        }
        Object.keys(node).forEach((k) => walk(node[k]));
      };
      walk(parsed);
      return this.cleanFileResources(acc);
    },
    isFileResource(obj) {
      if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
        return false;
      }
      const id = obj.id;
      if (id == null || id === "" || id === "default") {
        return false;
      }
      const path = obj.file_down_path || obj.fileDownPath;
      return !!path;
    },
    cleanFileResources(list) {
      const arr = (list || []).filter((p) => this.isFileResource(p));
      return _.uniqBy(arr, (p) => String(p.id));
    },
    validateProject() {
      let pageDatas = {};
      try {
        pageDatas = JSON.parse(this.canvasAllData || "{}") || {};
      } catch (e) {
        pageDatas = {};
      }
      if (
        this.componentDataPageId != null &&
        String(this.componentDataPageId) === String(this.selectedCurrentPage)
      ) {
        pageDatas[this.selectedCurrentPage] = this.componentData;
      }
      const pageIds = Object.keys(pageDatas);
      initEmptyErrorMapForPages(pageIds);
      this.$store.commit("setProjectValidateMap", {});
      for (let pageKey in pageDatas) {
        let currentPage = pageDatas[pageKey];
        if (!Array.isArray(currentPage)) {
          continue;
        }
        currentPage.forEach((comp) => {
          this.validateCompKeys(comp, "style", pageKey);
          this.validateCompKeys(comp, "networkConfig", pageKey);
        });
      }
      pruneCanvasErrorMap(pageDatas);
      this.$store.commit("setCurErrorMap");
    },
    validateCompKeys(comp, key, pageKey) {
      if (!comp[key]) {
        return;
      }
      for (let props in comp[key]) {
        if (!shouldValidateField(comp, key, props)) {
          continue;
        }
        this.validateProps(comp, key, props, pageKey);
      }
    },
    validateProps(comp, configKey, key, pageKey) {
      const validateFunc = getValidateFn(key);
      if (validateFunc && typeof validateFunc === "function") {
        eventBus.$emit("inputValidate", {
          key,
          val: comp[configKey][key],
          id: comp.id,
          otherVal: comp[configKey],
          pageKey,
        });
      }
    },
    estimateSaveButtonCount(toSave) {
      let local = 0;
      try {
        local = getComponentsByType("v-button", JSON.stringify(toSave || {})).length;
      } catch (e) {
        local = 0;
      }
      if (this.canvasLazyLoad) {
        return Math.max(local, Number(this.canvasServerButtonCount || 0));
      }
      return local;
    },
    getKeepPageIdSet() {
      const keep_page_ids = [];
      this.pageOptions.forEach((item) => {
        keep_page_ids.push(String(item.value));
        if (item.children && item.children.length) {
          item.children.forEach((c) => keep_page_ids.push(String(c.value)));
        }
      });
      return new Set(keep_page_ids);
    },
    buildMergedAllForSave() {
      let parsedAll = {};
      try {
        parsedAll = JSON.parse(this.canvasAllData || "{}") || {};
      } catch (e) {
        parsedAll = {};
      }
      const mergedAll = { ...parsedAll };
      if (
        this.selectedCurrentPage != null &&
        Array.isArray(this.componentData) &&
        this.componentData.length
      ) {
        mergedAll[String(this.selectedCurrentPage)] = this.componentData;
      }
      return mergedAll;
    },
    buildToSaveFromMerged(mergedAll) {
      const keepSet = this.getKeepPageIdSet();
      if (!this.canvasLazyLoad) {
        const out = {};
        Object.keys(mergedAll || {}).forEach((pageId) => {
          if (!keepSet.has(String(pageId))) {
            return;
          }
          out[pageId] = mergedAll[pageId];
        });
        return Object.keys(out).length ? out : mergedAll;
      }
      const toSave = {};
      Object.keys(mergedAll || {}).forEach((pageId) => {
        if (!keepSet.has(String(pageId))) {
          return;
        }
        const pageArr = mergedAll[pageId];
        const marked =
          this.canvasLoadedPageIds[String(pageId)] ||
          String(pageId) === String(this.selectedCurrentPage);
        if (marked && Array.isArray(pageArr) && !isPageSkeleton(pageArr)) {
          toSave[pageId] = pageArr;
        }
      });
      return toSave;
    },
    collectIntegrityIssuesForSave(toSave) {
      return collectProjectPageIntegrityIssues(
        toSave || {},
        null,
        (pageKey) =>
          walkPageLabel(this.pageOptions, pageKey) || String(pageKey)
      );
    },
    sortIntegrityIssuesByPageTree(issues) {
      const order = collectPageOptionIds(this.pageOptions).map((id) =>
        String(id)
      );
      return (issues || []).slice().sort((a, b) => {
        const ai = order.indexOf(String(a.pageKey));
        const bi = order.indexOf(String(b.pageKey));
        return (ai === -1 ? 99999 : ai) - (bi === -1 ? 99999 : bi);
      });
    },
    buildAllProjectPagesForIntegrity(mergedAll) {
      const keepSet = this.getKeepPageIdSet();
      const out = {};
      const missingLabels = [];
      keepSet.forEach((pageId) => {
        const key = String(pageId);
        let arr = mergedAll[key];
        if (arr == null && mergedAll && typeof mergedAll === "object") {
          for (const k of Object.keys(mergedAll)) {
            if (String(k) === key) {
              arr = mergedAll[k];
              break;
            }
          }
        }
        if (!Array.isArray(arr) || !arr.length) {
          missingLabels.push(
            walkPageLabel(this.pageOptions, key) || key
          );
          return;
        }
        if (isPageSkeleton(arr)) {
          missingLabels.push(
            walkPageLabel(this.pageOptions, key) || key
          );
          return;
        }
        out[key] = arr;
      });
      if (
        this.selectedCurrentPage != null &&
        Array.isArray(this.componentData) &&
        this.componentData.length
      ) {
        out[String(this.selectedCurrentPage)] = this.componentData;
      }
      return { pages: out, missingLabels };
    },
    async prepareAllPagesForSaveIntegrity() {
      await requestFlushCurrentPageBeforeSave();
      let loading;
      try {
        loading = this.$loading({
          lock: true,
          text: this.$tt("正在加载全部页面用于保存校验…"),
          spinner: "el-icon-loading",
        });
        await requestEnsureAllProjectPagesBeforeSave();
      } finally {
        if (loading) {
          loading.close();
        }
      }
      const merged = this.buildMergedAllForSave();
      const { pages, missingLabels } =
        this.buildAllProjectPagesForIntegrity(merged);
      if (missingLabels.length) {
        this.$notify({
          type: "warning",
          title: this.$tt("部分页面未加载完整"),
          message: this.$tt(
            `以下页面无法参与校验：${missingLabels.slice(0, 8).join("、")}${
              missingLabels.length > 8 ? "…" : ""
            }`
          ),
          duration: 8000,
          position: "top-left",
        });
      }
      return pages;
    },
    handleSaveData(allData) {
      var _this = this;
      if (!allData || typeof allData !== "object") {
        return allData || {};
      }
      // 先所有组件重置为false，再去读取有效配置设置为true，避免之前有true的值影响结果
      Object.keys(allData).forEach((pageId) => {
        if (!Array.isArray(allData[pageId])) {
          return;
        }
        allData[pageId].forEach((compItem, compInd) => {
          if (compItem && typeof compItem === "object") {
            allData[pageId][compInd].hasLinkEvents = false;
          }
        })
      })
      Object.keys(allData).forEach((pageId) => {
        if (!Array.isArray(allData[pageId])) {
          return;
        }
        allData[pageId].forEach((compItem) => {
          allData[pageId] = _this.setHasLinksEvents(compItem, allData[pageId]);
        })
      })
      // console.log('handleSaveData----', allData);
      return allData;
    },
    setHasLinksEvents(item, allComps) {
      if (item.componentLinks && item.componentLinks.links && item.componentLinks.links.length !== 0) {
        // 说明有组件联动数据属性
        item.componentLinks.links.forEach((linkItem) => {
          // 是当前组件
          if (linkItem.comp && linkItem.listenEvent && linkItem.changeAttrs && linkItem.changeAttrs.length !== 0) {
            var hasEffect = false;
            linkItem.changeAttrs.forEach((attr) => {
              if (attr.key && !isEmpty(attr.value)) {
                // 该属性有效
                hasEffect = true;
              }
            })
            if (hasEffect) {
              let ind = allComps.findIndex(a => a.id === item.id)
              if (ind !== -1) {
                allComps[ind].hasLinkEvents = true
              }
            }
          }
        });
      }
      return allComps;
    },
    async saveProject(options) {
      const silent = options && typeof options === "object" && !!options.silent;
      if (this.isSaving) {
        return false;
      }
      if (!this.projectName || this.projectName.trim() === '') {
        this.$message.error('工程名称不能为空');
        return false;
      }
      if (this.canvasPageSwitching) {
        this.$message.warning('页面切换中，请稍后再保存');
        return false;
      }

      let allPagesForIntegrity = await this.prepareAllPagesForSaveIntegrity();
      this.validateProject();
      pruneCanvasErrorMap(allPagesForIntegrity);
      if (hasCanvasValidationErrors()) {
        const first = describeCanvasValidationError(
          findFirstCanvasValidationError(),
          {
            pageOptions: this.pageOptions,
            canvasAllData: this.canvasAllData,
            selectedCurrentPage: this.selectedCurrentPage,
            componentData: this.componentData,
          }
        );
        const summary = (first && first.summary) || "有不正确的数据，请检查并修改后再保存";
        this.$notify({
          type: "error",
          title: "校验未通过，已定位到出错组件",
          message: summary,
          duration: 8000,
          position: "top-left",
        });
        eventBus.$emit("locateCanvasError", first);
        return false;
      }

      let integrityIssues = this.sortIntegrityIssuesByPageTree(
        this.collectIntegrityIssuesForSave(allPagesForIntegrity)
      );
      if (integrityIssues.length) {
        if (silent) {
          return false;
        }
        this.$notify({
          type: "warning",
          title: this.$tt("保存校验未通过"),
          message: this.$tt(summarizePageIntegrityIssues(integrityIssues)),
          duration: 8000,
          position: "top-left",
        });
        const integrityOk = await new Promise((resolve) => {
          this.$emit("integrity-save-flow", {
            issues: integrityIssues,
            done(ok) {
              resolve(!!ok);
            },
          });
        });
        if (!integrityOk) {
          this.$message.warning(
            this.$tt("已取消保存，请先处理页面数据异常")
          );
          return false;
        }
        allPagesForIntegrity = await this.prepareAllPagesForSaveIntegrity();
        integrityIssues = this.sortIntegrityIssuesByPageTree(
          this.collectIntegrityIssuesForSave(allPagesForIntegrity)
        );
        if (integrityIssues.length) {
          this.$message.warning(
            this.$tt("仍有页面数据异常，请重新确认后再保存")
          );
          return false;
        }
      }

      const { id } = this.$route.query;
      const { project_code } =
        (atob(this.$route.query.limits) &&
          JSON.parse(atob(this.$route.query.limits))) ||
        {};
      const { width, height } = this.projectStyleData || {};
      const interactionPackageData = sessionStorage.getItem("interactionPackageData");
      this.isSaving = true;
      this.pauseAutoSave = true;
      let isSuccess = false;
      try {
        const mergedAll = this.buildMergedAllForSave();
        let toSave = this.buildToSaveFromMerged(mergedAll);
        if (
          this.selectedCurrentPage != null &&
          Array.isArray(this.componentData) &&
          this.componentData.length
        ) {
          toSave[String(this.selectedCurrentPage)] = this.componentData;
        }
        const { pages: allPagesFinal } =
          this.buildAllProjectPagesForIntegrity(mergedAll);
        const lastCheck = this.collectIntegrityIssuesForSave(allPagesFinal);
        if (lastCheck.length) {
          this.$message.warning(
            this.$tt("保存校验未通过，请处理页面数据异常")
          );
          return false;
        }
        const page_sort = this.pageOptions.map((item) => item.value);
        const keep_page_ids = [];
        this.pageOptions.forEach((item) => {
          keep_page_ids.push(String(item.value));
          if (item.children && item.children.length) {
            item.children.forEach((c) => keep_page_ids.push(String(c.value)));
          }
        });
        var panel_data = this.handleSaveData(toSave);
        // 从画布清洗图片资源写入 file_resouces；懒保存未加载页仍保留工程里已有资源
        const fromCanvas = this.getAllPictures(panel_data);
        const allPictures = this.cleanFileResources(
          this.canvasLazyLoad
            ? [...fromCanvas, ...(this.projectFileResouces || [])]
            : fromCanvas
        );
        this.$store.commit("setProjectFileResouces", allPictures);
        let res = await this.$request.appOrUpdateProject({
          id: this.dataId || id,
          project_name: this.projectName,
          project_width: width + "",
          project_height: height + "",
          file_resouces: allPictures,
          page_sort,
          keep_page_ids,
          panel_data: JSON.stringify(panel_data),
          interaction_package_data: interactionPackageData,
          type: project_code,
          lazy_save: !!this.canvasLazyLoad,
          button_count: this.estimateSaveButtonCount(toSave),
        });
        if (res.status != "success") {
          this.$message.error(res.msg);
          return;
        } else {
          if (!silent) {
            this.$message.success(res.msg || "已提交保存");
          }
          const prevId = this.dataId || id;
          this.dataId = res.data && res.data.id;
          if (this.dataId && prevId && String(this.dataId) !== String(prevId)) {
            this.$router.replace({
              query: { ...this.$route.query, id: this.dataId, temporaryId: undefined },
            });
          }
          this.syncRouteAfterTemplateSave(this.dataId);
          // 只清 Redis 草稿，绝不能 removeCache（会把当前画布/页树全部清空）
          if (this.cacheCanvasDataKey) {
            this.$request.removeProjectCache(this.cacheCanvasDataKey).then(() => {
              this.lastAutoSavePayload = null;
              this.lastRedisPayload = null;
            }).catch(() => {});
          }
          this.capturePersistBaseline();
        }
        isSuccess = true;
      } catch (error) {
        this.$message.error('保存失败');
      } finally {
        this.isSaving = false;
        this.pauseAutoSave = false;
      }
      return isSuccess;
      // this.$request
      //   .appOrUpdateProject({
      //     id: id || this.dataId,
      //     project_name: this.projectName,
      //     project_width: width + "",
      //     project_height: height + "",
      //     file_resouces: allPictures,
      //     page_sort: this.pageOptions.map((item) => item.value),
      //     panel_data: JSON.stringify({
      //       ...JSON.parse(data),
      //       [this.selectedCurrentPage]: this.componentData,
      //     }),
      //     type: project_code,
      //   })
      //   .then((res) => {
      //     if (res.status != "success") {
      //       this.$message.error(res.msg);
      //       return;
      //     } else {
      //       this.$message.success('保存成功');
      //       this.dataId = res.data && res.data.id;
      //     }
      //     // this.$router.push("/projects");
      //   })
      //   .finally(() => {
      //     this.isSaving = false;
      // this.$changeLoadingState(false);
      //   });
    },
    clearLocalstorage() {
      this.clearCanvas();
      this.$store.commit("setPageOptions", [
        {
          label: "新页面1",
          value: 1,
        },
      ]);
      this.$store.commit("setCanvasStyle", {
        width: 1200,
        height: 740,
        scale: 100,
      });
      this.$store.commit("setProjectStyle", {
        width: 1200,
        height: 740,
        scale: 100,
      });
      this.$store.commit("setSelectedCurrentPage", 1);
      clearCanvasSessionStorage();
    },
    closeEditProject() {},
    format(value) {
      const scale = this.scale;
      return (value * parseInt(scale)) / 100;
    },

    getOriginStyle(value) {
      const scale = this.canvasStyleData.scale;
      const result = value / (parseInt(scale) / 100);
      return result;
    },

    handleScaleChange() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        // 画布比例设一个最小值，不能为 0
        // eslint-disable-next-line no-bitwise
        this.scale = ~~this.scale || 1;
        const componentData = deepCopy(this.componentData);
        componentData.forEach((component) => {
          Object.keys(component.style).forEach((key) => {
            if (this.needToChange.includes(key)) {
              // 根据原来的比例获取样式原来的尺寸
              // 再用原来的尺寸 * 现在的比例得出新的尺寸
              // 不能用 Math.round 防止 1 px 的边框变 0
              component.style[key] = Math.ceil(
                this.format(this.getOriginStyle(component.style[key]))
              );
            }
          });
        });

        this.$store.commit("setComponentData", componentData);
        this.$store.commit("setCanvasStyle", {
          ...this.canvasStyleData,
          scale: this.scale,
        });
        this.$store.commit("setProjectStyle", {
          ...this.projectStyleData,
          scale: this.scale,
        });
      }, 1000);
    },

    lock() {
      if (this.curComponent.id === 'Back') {
        this.$message.error('请先点击选择要锁定组件');
        return;
      }
      this.$store.commit("lock");
      if (this.curComponent.compType === ECOLOGICAL_SIGN) {
        // 生态组件要将子组件下的一并处理了
        this.curComponent.propValue.map((item, index) => {
          this.$store.commit("setEcologicalChildData", {
            index: index,
            key: 'isLock',
            value: true
          });
        });
      }
    },
    toside(key) {
      this.$store.commit("componentsToSide", key);
    },

    unlock() {
      if (this.curComponent.id === 'Back') {
        this.$message.error('请先点击选择要锁定组件');
        return;
      }
      this.$store.commit("unlock");
      if (this.curComponent.compType === ECOLOGICAL_SIGN) {
        // 生态组件要将子组件下的一并处理了
        this.curComponent.propValue.map((item, index) => {
          this.$store.commit("setEcologicalChildData", {
            index: index,
            key: 'isLock',
            value: false
          });
        });
      }
    },

    compose() {
      this.$store.commit("compose");
      this.$store.commit("recordSnapshot");
    },

    decompose() {
      this.$store.commit("decompose");
      this.$store.commit("recordSnapshot");
    },

    undo() {
      this.$store.commit("undo");
    },

    redo() {
      this.$store.commit("redo");
    },

    handleFileChange(e) {
      const file = e.target.files[0];
      if (!file.type.includes("image")) {
        toast("只能插入图片");
        return;
      }

      const reader = new FileReader();
      reader.onload = (res) => {
        const fileResult = res.target.result;
        const img = new Image();
        img.onload = () => {
          this.$store.commit("addComponent", {
            component: {
              ...commonAttr,
              id: generateID(),
              component: "Picture",
              label: "图片",
              icon: "",
              propValue: fileResult,
              style: {
                ...commonStyle,
                top: 0,
                left: 0,
                width: img.width,
                height: img.height,
              },
            },
          });

          this.$store.commit("recordSnapshot");

          // 修复重复上传同一文件，@change 不触发的问题
          document.querySelector("#input").setAttribute("type", "text");
          document.querySelector("#input").setAttribute("type", "file");
        };

        img.src = fileResult;
      };

      reader.readAsDataURL(file);
    },

    preview() {
      this.isShowPreview = true;
      this.$store.commit("setEditMode", "preview");
    },

    async save(hideTip, notSetRequest) {
      if (this.pauseAutoSave || this.$store.state.canvasEditorLeaving) {
        return;
      }
      if (hideTip && !this.projectDirty) {
        return;
      }
      if (this.isSaving) {
        return;
      }
      if (this.canvasPageSwitching) {
        return;
      }
      if (!this.selectedCurrentPage) {
        if (!hideTip) {
          this.showSaveSuccessTip();
        }
        return;
      }
      let remainComponentData = {};
      try {
        remainComponentData = this.canvasAllData
          ? JSON.parse(this.canvasAllData)
          : {};
      } catch (e) {
        remainComponentData = {};
      }
      const remainErrorData =
        (sessionStorage.getItem("canvasErrorMap") &&
          JSON.parse(sessionStorage.getItem("canvasErrorMap"))) ||
        {};
      if (!remainErrorData[this.selectedCurrentPage]) {
        sessionStorage.setItem(
          "canvasErrorMap",
          JSON.stringify({
            ...remainErrorData,
            [this.selectedCurrentPage]: {},
          })
        );
        this.$store.commit("setCurErrorMap");
      }

      // 懒加载下 canvasAllData 已是「当前页完整 + 其它页骨架」，直接合并当前页即可
      // 切页过程中 componentData 可能仍属上一页，禁止写进当前 selectedCurrentPage
      const canMergeCurrent =
        this.componentDataPageId != null &&
        String(this.componentDataPageId) === String(this.selectedCurrentPage);
      const datas = JSON.stringify({
        ...remainComponentData,
        ...(canMergeCurrent
          ? { [this.selectedCurrentPage]: this.componentData }
          : {}),
      });

      // 无变更：跳过 vuex / redis，避免 1s 空转
      if (hideTip && datas === this.lastAutoSavePayload) {
        return;
      }
      this.lastAutoSavePayload = datas;

      if (notSetRequest) {
        this.$store.commit("setCanvasAllData", datas);
      } else {
        // Redis 内容与上次相同则跳过网络
        if (hideTip && datas === this.lastRedisPayload) {
          this.$store.commit("setCanvasAllData", datas);
        } else {
          this.$store.commit("setCanvasAllData", datas);
          await this.setCanvasCache(datas);
        }
      }

      const remainCanvasStyle =
        (sessionStorage.getItem("canvasStyle") &&
          JSON.parse(sessionStorage.getItem("canvasStyle"))) ||
        {};
      sessionStorage.setItem(
        "canvasStyle",
        JSON.stringify({
          ...remainCanvasStyle,
          [1]: this.canvasStyleData[1] || this.canvasStyleData,
        })
      );

      if (!hideTip) {
        this.showSaveSuccessTip();
      }
    },
    showSaveSuccessTip() {
      this.$message({
        type: "success",
        message: "保存成功",
        duration: 2000,
        customClass: "canvas-save-success-tip",
      });
    },
    async setCanvasCache(data) {
      if (this.$store.state.canvasEditorLeaving || this.pauseAutoSave) {
        return;
      }
      this.$store.commit("setCanvasAllData", data);
      if (!this.cacheCanvasDataKey) {
        return;
      }
      await coalescedSetProjectCache(this.$request, {
        key: this.cacheCanvasDataKey,
        value: data,
        checkLeaving: () =>
          this.$store.state.canvasEditorLeaving || this.pauseAutoSave,
        onWritten: (payload) => {
          this.lastRedisPayload = payload;
        },
      });
    },
    clearCanvas() {
      this.$store.commit("setCurComponent", {
        component: BACK_COMPONENT(),
      });
      this.$store.commit("setComponentData", [BACK_COMPONENT()]);
      this.$store.commit("recordSnapshot");
      sessionStorage.removeItem("canvasErrorMap");
    },

    handlePreviewChange() {
      this.$store.commit("setEditMode", "edit");
    },
  },
};
</script>
<style lang="scss" scoped>
.top-container {
  background: #fff;
  border-bottom: 1px solid #ebeef5;
}
.toolbar-tools {
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 4px;
  padding: 2px 0;
  white-space: nowrap;
}
.tool-label-btn {
  margin: 0;
  flex-shrink: 0;
  i {
    margin-right: 4px;
  }
}
.align-menu-trigger {
  .align-menu-arrow {
    margin-left: 2px;
    margin-right: 0;
    font-size: 12px;
  }
}
.oper-tool-dropdown {
  margin-right: 10px;
}
</style>
<style lang="scss">
.jhb-tip-content {
  line-height: 24px;
}
.jhb-tip-btn, .jhb-tip-btn:hover {
  font-size: 16px;
  color: red;
}
.toolbar-align-popover {
  padding: 0;
  min-width: 280px;
}
.align-menu-panel {
  padding: 10px 12px 12px;
  .align-menu-section + .align-menu-section {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #ebeef5;
  }
  .align-menu-title {
    font-size: 12px;
    color: #909399;
    margin-bottom: 8px;
    line-height: 1;
  }
  .align-menu-grid {
    display: grid;
    gap: 8px;
    &--5 {
      grid-template-columns: repeat(5, 1fr);
    }
    &--2 {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  .align-menu-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    min-height: 56px;
    padding: 6px 4px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    background: #fff;
    color: #606266;
    cursor: pointer;
    transition: all 0.15s ease;
    i {
      font-size: 18px;
      line-height: 1;
    }
    span {
      font-size: 12px;
      line-height: 1.2;
      white-space: nowrap;
    }
    &:hover:not(:disabled) {
      color: #409eff;
      border-color: #c6e2ff;
      background: #ecf5ff;
    }
    &:disabled {
      color: #c0c4cc;
      border-color: #ebeef5;
      background: #f5f7fa;
      cursor: not-allowed;
    }
  }
}
.toolbar {
  padding: 8px 10px;
  white-space: nowrap;
  overflow-x: auto;
  background: #fff;
  border-bottom: 1px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  .canvas-config {
    display: inline-block;
    margin-left: 10px;
    font-size: 14px;
    color: #606266;

    input {
      width: 50px;
      margin-left: 10px;
      outline: none;
      padding: 0 5px;
      border: 1px solid #ddd;
      color: #606266;
    }

    span {
      margin-left: 10px;
    }
  }

  .insert {
    display: inline-block;
    line-height: 1;
    white-space: nowrap;
    cursor: pointer;
    background: #fff;
    border: 1px solid #dcdfe6;
    color: #606266;
    -webkit-appearance: none;
    text-align: center;
    box-sizing: border-box;
    outline: 0;
    margin: 0;
    transition: 0.1s;
    font-weight: 500;
    padding: 9px 15px;
    font-size: 12px;
    border-radius: 3px;
    margin-left: 10px;

    &:active {
      color: #3a8ee6;
      border-color: #3a8ee6;
      outline: 0;
    }

    &:hover {
      background-color: #ecf5ff;
      color: #3a8ee6;
    }
  }
}
.toolBarTitle {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  flex-wrap: nowrap;
  box-sizing: border-box;
  .el-button--mini {
    padding: 7px 8px;
  }
  .el-input-number.is-without-controls .el-input__inner {
    padding: 0px 8px;
  }
  .toolbar-meta {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 8px;
    .toolbar-back-btn {
      margin: 0;
    }
    .prop-config-form {
      display: flex;
      flex-wrap: nowrap;
      .el-form-item--mini.el-form-item {
        margin-bottom: 0px;
        margin-right: 8px;
      }
      .el-input-number .el-input__inner {
        text-align: left;
      }
    }
  }
  .toolbar-tools-scroll {
    flex: 1;
    min-width: 0;
    overflow-x: auto;
    overflow-y: hidden;
  }
  .toolbar-actions-right {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 8px;
    .toolbar-right-btn {
      margin: 0;
    }
  }
  .titleAndName {
    display: flex;
    align-items: center;
    .title {
      font-size: 22px;
      font-weight: 700;
      line-height: 40px;
      display: inline-block;
      -webkit-box-flex: 0;
      flex-grow: 0;
      flex-shrink: 0;
      margin-right: 20px;
    }
    .prop-config-form {
      .el-form-item--mini.el-form-item {
        margin-bottom: 0px;
      }
      .el-input-number .el-input__inner {
        text-align: left;
      }
    }
    & span {
      flex-shrink: 0;
    }
    .searchInput {
      font-size: 18px;
      background-color: rgb(238, 238, 238);
      border-radius: 40px;
      overflow: hidden;
      display: flex;
      height: 42px;
      .el-input-group__prepend {
        color: #222;
        width: 70px;
        font-size: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .el-input {
        text-align: center;
        color: rgb(34, 34, 34);
        border-width: initial;
        border-style: none;
        border-color: initial;
        border-image: initial;
      }
      .el-input__inner {
        border-radius: 0 24px 24px 0;
        height: 42px !important;
      }
    }
  }
  .pageSizeConfig {
    display: flex;
    .config {
      margin-left: 12px;
      display: flex;
      align-items: center;
      .label {
        font-size: 16px;
        color: #222;
        margin-right: 6px;
      }
      .el-input {
        width: 150px;
      }
    }
  }
  // .saveProject {
  //   height: 42px;
  //   width: 104px;
  //   font-size: 16px;
  //   margin-left: 12px;
  // }
}
.closeDialog {
  .el-dialog__body {
    text-align: center;
    font-size: 16px;
  }
  .el-dialog__headerbtn {
    font-size: 20px;
  }
}
.canvas-save-success-tip {
  top: auto !important;
  bottom: 32px !important;
  left: 50% !important;
  transform: translateX(-50%);
  min-width: 140px !important;
  padding: 12px 20px !important;
  box-shadow: 0 4px 16px rgba(103, 194, 58, 0.25);
}
.saveMessage {
  top: 66px;
  z-index: 2000;
  left: 70px;
  min-width: 100px !important;
}
</style>
